import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Loader2, Save } from 'lucide-react'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCreatePatient } from '@/hooks/usePatients'
import { PageHeading } from '@/components/layout/PageHeading'
import {
  Field,
  PatientDemographicsFields,
  type DemographicsSectionProps,
} from '@/components/patient/PatientDemographicsFields'
import {
  BIRTH_DATE_FUTURE_MESSAGE,
  EMPTY_DEMOGRAPHICS,
  birthDateError,
  toCreatePayload,
  type DemographicFormField,
  type DemographicsForm,
} from '@/lib/patientForm'

function Section({ title, description, icon: Icon, children }: DemographicsSectionProps) {
  return (
    <div className='rounded-xl border bg-card p-5 shadow-sm'>
      <div className='mb-4 flex items-center gap-2.5'>
        <span className='flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground'>
          <Icon className='h-4 w-4' />
        </span>
        <div>
          <h2 className='text-sm font-semibold leading-none'>{title}</h2>
          {description && (
            <p className='mt-1 text-xs text-muted-foreground'>{description}</p>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}

export function PatientCreatePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const create = useCreatePatient()

  // Seeded from the list page, which carries over whatever was typed in its search box.
  // Every field except the MRN is optional: a patient may be registered before
  // anything else is known.
  const [mrnInput, setMrnInput] = useState(searchParams.get('mrn') ?? '')
  const [form, setForm] = useState<DemographicsForm>(EMPTY_DEMOGRAPHICS)
  const [dobError, setDobError] = useState<string | null>(null)
  const set = (field: DemographicFormField, value: string) => {
    if (field === 'birthDate') setDobError(null)
    setForm(f => ({ ...f, [field]: value }))
  }

  const mrn = mrnInput.trim()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // Presence is the only client-side rule left. There used to be a format
    // check for `MRN-<identifier>` here, mirroring one on the server; both are
    // gone (z8nrz7dyc2) — an MRN is issued by the hospital, and a real Epic one
    // is a bare `1234567`.
    //
    // The submit button is `disabled` while this is empty, so this guard is
    // belt-and-braces rather than the thing the operator sees.
    if (!mrn) return
    // A date of birth in the future is refused here with a message (z8nrz7dp77
    // AC9) — the server refuses it too, but the operator should not have to
    // submit to find out.
    const dob = birthDateError(form.birthDate)
    if (dob) {
      setDobError(dob)
      return
    }
    // A duplicate MRN comes back as a 409; its message is surfaced below rather
    // than being pre-empted by a client-side uniqueness guess.
    const created = await create.mutateAsync(toCreatePayload(mrn, form)).catch(() => null)
    if (created) navigate(`/patients/${encodeURIComponent(created.mrn)}`)
  }

  return (
    <div className='min-h-full bg-muted/20'>
      <form onSubmit={e => void handleSubmit(e)}>
        <div className='max-w-4xl py-8 pl-6 pr-4'>
          <Breadcrumbs
            items={[{ label: 'Patients', to: '/patients' }, { label: 'New patient' }]}
            className='mb-4'
          />

          <PageHeading
            className='mb-6'
            title='New patient'
            description='Only the MRN is required. Everything else can be filled in later, or arrive automatically from the EHR integration.'
          />

          {create.error && (
            <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              {create.error instanceof Error
                ? create.error.message
                : 'Failed to create patient.'}
            </div>
          )}

          <div className='space-y-4'>
            <PatientDemographicsFields
              form={form}
              onChange={set}
              birthDateError={dobError}
              section={Section}
              onBirthDateInvalid={() => setDobError(BIRTH_DATE_FUTURE_MESSAGE)}
              identityLead={
                // No field-level error state here any more: the format rule
                // was its only source (z8nrz7dyc2). What can still fail is a
                // duplicate, which the server answers 409 for and which the
                // banner above already carries.
                <Field
                  id='patient-mrn'
                  label='MRN'
                  required
                  hint='As the hospital issued it. Any format is accepted.'
                >
                  <Input
                    id='patient-mrn'
                    value={mrnInput}
                    onChange={e => setMrnInput(e.target.value)}
                    placeholder='e.g. 1234567'
                    autoFocus
                  />
                </Field>
              }
            />
          </div>
        </div>

        <div className='sticky bottom-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80'>
          <div className='flex max-w-4xl items-center justify-between gap-4 py-3 pl-6 pr-4'>
            <p className='hidden text-xs text-muted-foreground sm:block'>
              Only the MRN is required.
            </p>
            <div className='flex items-center gap-2'>
              <Button type='button' variant='ghost' onClick={() => navigate('/patients')}>
                <ArrowLeft className='mr-2 h-4 w-4' />
                Cancel
              </Button>
              <Button type='submit' disabled={!mrn || create.isPending}>
                {create.isPending ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <Save className='mr-2 h-4 w-4' />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
